// @givmo/board — one committee: its members, its charter, its rules, and the organization's line-8b
// answer (CBG-YR6-REG frame c2; CBG-YR6-AUTH frame c6; `board-committees.md` §3.2).
//
// Linear's project page is the shape taken: who serves on the committee in the main column, and what
// is RECORDED about it — the answer, the charter, its rules — as a quiet column of facts beside it,
// stacking under it on a phone. Its meetings, its decisions for the board and "Schedule a meeting" are
// CBG-YR6-MEET's and -CONFIRM's (C7), so the main column holds the members alone for now.
//
// The charter is the governing-document chain whose label the committee holds, read through the
// registry read: the version in force and "Add a version". The registry leaves drafts out (a draft
// is not a version), so the Governance page's own read of the drafts names one saved under the
// committee's label: "A draft is in your minute book", and nothing that would start a second. With
// neither: "Start from a form", "Import one adopted before Givmo", or "Link a charter already
// recorded". A committee added from a form
// arrives with `?charter=<form id>`: its version dialog opens on that form — unless a charter is
// already recorded under the committee's name, which the committee then holds as it stands (§4.1).
//
// Every control renders only for a holder of the ONE string its operation's decorator names:
// composition `board_member:manage`, a rule and the answer `board_governance_parameter:manage`, a
// charter's draft `board_record:write` and its import `board_record:import`, and (CBG-YR6-MEET)
// "Schedule a meeting" `board_meeting:write`.
//
// CBG-YR6-MEET (frame c2): the committee's meetings that stand, the latest first, each with where
// its minutes are, from the committee read itself (`meetings`; a meeting still ahead says nothing of
// minutes). "Schedule a meeting" opens the schedule dialog on this committee. CBG-YR6-APPROVE: while
// no rule of its own is recorded, its minutes are approved your board's way, said as that.
//
// CBG-YR6-CONFIRM (frame c2): while the committee's practice is on, "Decisions for the board" — the
// decisions recorded under it, newest first, each with its day and tally and where it is: suggested
// for the board meeting the backend names, or on a board meeting's agenda with the board's motion
// under that item as recorded. Nothing says a decision is confirmed or not: it binds as recorded.
// The practice is the Owner's (`board_member:manage`): "Change" beside its line, or, while it's off,
// "Suggest its decisions for the board…" in the "⋯" menu (`confirmation-dialog.tsx`). A dissolved
// committee's decisions are suggested no more: its card keeps them and where each went, without the
// practice line, and goes when there is none.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  isGivmoError,
  type BoardCommitteeMemberOut,
  type BoardCommitteeOut,
  type OrgBoardGoverningDocumentOut,
  type OrgBoardRecordOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Icon,
  IconButton,
  Menu,
  Skeleton,
} from '@givmo/design-system';
import { MoreHorizontal, Pencil } from 'lucide-react';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { Link, useNavigate, useParams, useSearch } from '../../board-routing';
import { GroupLabel } from '../../components/fine-print';
import { PageShell } from '../../components/page-shell';
import { displayDate } from '../../lib/display';
import { VersionDialog, type VersionDialogMode } from '../govdocs/version-dialog';
import { MeetingFormDialog } from '../meetings/meeting-form-dialog';
import { keepRangesTogether } from '../meetings/keep-ranges-together';
import { meetingDay, meetingHasStarted, meetingTypeLabel } from '../meetings/meetings-decode';
import { approvalRule, extractParameters } from '../parameters/parameters-decode';
import { committeeMeetingsWords as cm } from './committee-meetings-words';
import { extractRecords } from '../records/records-decode';
import { AuthorityDialog, type CharterInForce } from './authority-dialog';
import {
  CharterLinkDialog,
  CommitteeDetailsDialog,
  CorrectDissolutionDialog,
  DissolveDialog,
} from './committee-dialogs';
import { CommitteeRuleDialog } from './committee-rule-dialog';
import {
  committeeDecisionLines,
  practiceOf,
  type CommitteeDecisionLine,
} from './committee-confirmation-decode';
import { committeeConfirmationWords as cw } from './committee-confirmation-words';
import { ConfirmationDialog } from './confirmation-dialog';
import {
  activeMembers,
  endedMembers,
  isDissolved,
  memberDates,
  memberFacts,
  ruleInForce,
} from './committees-decode';
import { MemberDialog } from './member-dialog';
import { committeesWords as w } from './committees-words';

const CARD_TITLE = 'font-sans text-s15 font-w700 tracking-givmo text-text-strong';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const PLAIN = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';
const LINK =
  'font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';
const BACK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';

type Opened =
  | { kind: 'member'; member: BoardCommitteeMemberOut | null }
  | {
      kind:
        | 'details'
        | 'link'
        | 'dissolve'
        | 'correct'
        | 'authority'
        | 'rule'
        | 'schedule'
        | 'practice';
    }
  | { kind: 'version'; mode: VersionDialogMode; formId?: string };

/** The committee's charter chain in the registry, by the label it holds. */
function chainOf(
  chains: readonly OrgBoardGoverningDocumentOut[],
  label: string | null | undefined,
): OrgBoardGoverningDocumentOut | null {
  if (!label) return null;
  return chains.find((chain) => (chain.label ?? '').trim() === label.trim()) ?? null;
}

/** A draft of the committee's charter: a version not yet adopted, saved under the label it holds. */
interface CharterDraft {
  recordId: string;
  title: string;
}

function draftOf(
  drafts: readonly OrgBoardRecordOut[],
  label: string | null | undefined,
): CharterDraft | null {
  if (!label) return null;
  const found = drafts.find(
    (record) =>
      record.governing_document_kind === 'committee_charter' &&
      (record.governing_document_label ?? '').trim() === label.trim(),
  );
  return found === undefined ? null : { recordId: found.id, title: found.title || label };
}

function charterInForce(chain: OrgBoardGoverningDocumentOut | null): CharterInForce | null {
  const version = chain?.version_in_force ?? null;
  if (version === null || version === undefined) return null;
  return {
    recordId: version.record_id,
    title: (version.record?.title ?? '').trim() || (chain?.label ?? ''),
    adoptedOn: version.adopted_on ?? null,
  };
}

export function CommitteePage() {
  const { committeeId } = useParams({ from: '/committees/$committeeId' });
  const search = useSearch();
  const navigate = useNavigate();
  const { orgRef, isReady, permissions } = useBoardHost();
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const canManage = hasBoardPermission(permissions, 'board_member:manage');
  // CBG-UX9: a new seat's invitation, asked after the add, is the invitation's own string.
  const canInvite = hasBoardPermission(permissions, 'board_member:grant_access');
  const canRecordRules = hasBoardPermission(permissions, 'board_governance_parameter:manage');
  const canDraft = hasBoardPermission(permissions, 'board_record:write');
  const canImport = hasBoardPermission(permissions, 'board_record:import');
  const canSchedule = hasBoardPermission(permissions, 'board_meeting:write');
  const [opened, setOpened] = useState<Opened | null>(null);
  const org = orgRef ?? '';

  const detailOptions = operations.officerGetBoardCommitteeOptions({
    path: { org_ref: org, committee_id: committeeId },
  });
  const detailQuery = useQuery({ ...detailOptions, enabled: isReady && orgRef !== null });
  const chartersQuery = useQuery({
    queryKey: ['board-committee-charters', org],
    enabled: isReady && orgRef !== null,
    queryFn: async (): Promise<OrgBoardGoverningDocumentOut[]> => {
      const { data } = await operations.officerListBoardGoverningDocuments({
        path: { org_ref: org },
        query: { document_kind: 'committee_charter' },
        throwOnError: true,
      });
      return Array.isArray(data) ? data : [];
    },
  });

  // The Governance page's read of the drafts, on its own key: a draft saved there shows here.
  const draftsQuery = useQuery({
    queryKey: ['board-records', org, 'governing-document-drafts'],
    enabled: isReady && orgRef !== null,
    queryFn: async (): Promise<OrgBoardRecordOut[]> => {
      const { data } = await operations.officerListBoardRecords({
        path: { org_ref: org },
        query: { record_kind: 'governing_document', 'page[limit]': 100 },
        throwOnError: true,
      });
      return extractRecords(data).filter(
        (record) => record.state === 'draft' || record.state === 'submitted',
      );
    },
  });

  // CBG-YR6-APPROVE: the board's own way for its minutes, which stands in for the committee's until
  // the committee records one.
  const parametersQuery = useQuery({
    ...operations.officerListBoardGovernanceParametersOptions({ path: { org_ref: org } }),
    enabled: isReady && orgRef !== null,
    select: extractParameters,
  });
  const boardsWay = parametersQuery.isSuccess
    ? approvalRule(parametersQuery.data, 'minutes_approval').way
    : null;

  const committee = detailQuery.data ?? null;
  const charters = useMemo(() => chartersQuery.data ?? [], [chartersQuery.data]);
  const chain = chainOf(charters, committee?.charter_label);
  const charter = charterInForce(chain);
  const draft = draftOf(draftsQuery.data ?? [], committee?.charter_label);

  /** A write's answer is the committee as it now stands: show it, and refresh what reads it. */
  const show = (updated: BoardCommitteeOut) => {
    queryClient.setQueryData(detailOptions.queryKey, updated);
    void queryClient.invalidateQueries({
      queryKey: operations.officerListBoardCommitteesOptions({ path: { org_ref: org } }).queryKey,
    });
  };
  const settle = (updated: BoardCommitteeOut) => {
    show(updated);
    setOpened(null);
  };

  // `?charter=<form id>`: a committee added from a form opens its version dialog on that form —
  // unless a charter (or a draft of one) is already recorded under its name (§4.1), which it then
  // holds as it stands.
  const startedFromForm = useRef(false);
  const formId = typeof search.charter === 'string' ? search.charter : null;
  useEffect(() => {
    if (formId === null || startedFromForm.current) return;
    if (committee === null || !chartersQuery.isSuccess || draftsQuery.isPending) return;
    startedFromForm.current = true;
    void navigate({ to: '/committees/$committeeId', params: { committeeId }, replace: true });
    if (charter === null && draft === null && canDraft && !isDissolved(committee)) {
      setOpened({ kind: 'version', mode: 'draft', formId });
    }
  }, [
    formId,
    committee,
    chartersQuery.isSuccess,
    draftsQuery.isPending,
    charter,
    draft,
    canDraft,
    navigate,
    committeeId,
  ]);

  // Starting a charter for a committee that holds no label first gives it its own name's label.
  const holdLabel = useMutation({
    mutationFn: async (next: Opened) => {
      if (committee !== null && !committee.charter_label) {
        const { data } = await operations.officerLinkBoardCommitteeCharter({
          path: { org_ref: org, committee_id: committee.id },
          body: { label: committee.name },
          throwOnError: true,
        });
        queryClient.setQueryData(detailOptions.queryKey, data);
      }
      return next;
    },
    onSuccess: (next) => setOpened(next),
  });

  if (detailQuery.isError) {
    return (
      <PageShell title={w.pageTitle} testId="page-committee">
        <ErrorMessage testId="committee-not-found">{w.notFound}</ErrorMessage>
      </PageShell>
    );
  }
  if (committee === null) {
    return (
      <PageShell title={w.pageTitle} testId="page-committee">
        <Skeleton className="h-[240px] w-full rounded-lg" testId="committee-loading" />
      </PageShell>
    );
  }

  const dissolved = isDissolved(committee);
  const established = committee.established_on ? displayDate(committee.established_on) : null;
  const practice = practiceOf(committee);

  return (
    <PageShell
      title={committee.name}
      description={
        <span className="flex flex-col gap-2">
          <span>{w.headerLine(committee.continuity, established)}</span>
          {dissolved && committee.dissolved_on ? (
            <span data-testid="committee-dissolved-line">
              {w.dissolvedHeader(displayDate(committee.dissolved_on))}
            </span>
          ) : null}
        </span>
      }
      actions={
        <div className="flex items-center gap-12">
          <Link to="/committees" exact className={BACK} data-testid="committee-back">
            {w.allCommittees}
          </Link>
          {canSchedule && !dissolved ? (
            <Button
              appearance="saas"
              tone="primary"
              onClick={() => setOpened({ kind: 'schedule' })}
              testId="committee-schedule"
            >
              {cm.scheduleMeeting}
            </Button>
          ) : null}
          {canManage ? (
            <Menu.Root>
              <Menu.Trigger
                render={
                  <IconButton
                    aria-label={w.moreActions}
                    tone="secondary"
                    size="md"
                    testId="committee-more"
                  />
                }
              >
                <Icon icon={MoreHorizontal} size={18} aria-hidden />
              </Menu.Trigger>
              <Menu.Content saas align="end" className="min-w-[220px]" testId="committee-more-menu">
                {dissolved ? (
                  <Menu.Item
                    saas
                    onClick={() => setOpened({ kind: 'correct' })}
                    testId="committee-correct"
                  >
                    {w.correct}
                  </Menu.Item>
                ) : (
                  <>
                    <Menu.Item
                      saas
                      onClick={() => setOpened({ kind: 'details' })}
                      testId="committee-change"
                    >
                      {w.changeDetails}
                    </Menu.Item>
                    <Menu.Item
                      saas
                      onClick={() => setOpened({ kind: 'link' })}
                      testId="committee-link"
                    >
                      {w.linkCharter}
                    </Menu.Item>
                    {!practice.on ? (
                      <Menu.Item
                        saas
                        onClick={() => setOpened({ kind: 'practice' })}
                        testId="committee-practice"
                      >
                        {cw.menuItem}
                      </Menu.Item>
                    ) : null}
                    <Menu.Separator className="my-4 bg-border-subtle" />
                    <Menu.Item
                      saas
                      tone="danger"
                      onClick={() => setOpened({ kind: 'dissolve' })}
                      testId="committee-dissolve"
                    >
                      {w.dissolve}
                    </Menu.Item>
                  </>
                )}
              </Menu.Content>
            </Menu.Root>
          ) : null}
        </div>
      }
      testId="page-committee"
    >
      <div className="grid grid-cols-[minmax(0,1fr)_340px] items-start gap-20 max-lg:grid-cols-1">
        <div className="flex min-w-0 flex-col gap-20">
          <MembersCard
            committee={committee}
            canManage={canManage && !dissolved}
            onAdd={() => setOpened({ kind: 'member', member: null })}
            onChange={(member) => setOpened({ kind: 'member', member })}
          />
          <MeetingsCard committee={committee} />
          {/* A dissolved committee's card stays only for decisions already recorded under the
              practice, as its history: nothing is suggested from it any more. */}
          {practice.on && (!dissolved || committeeDecisionLines(committee).length > 0) ? (
            <DecisionsCard
              committee={committee}
              citation={practice.citation}
              canChange={canManage && !dissolved}
              dissolved={dissolved}
              onChange={() => setOpened({ kind: 'practice' })}
            />
          ) : null}
        </div>
        <div className="flex min-w-0 flex-col gap-20">
          {committee.authority_question_offered ? (
            <AuthorityCard
              committee={committee}
              canRecord={canRecordRules}
              onRecord={() => setOpened({ kind: 'authority' })}
            />
          ) : null}
          <CharterCard
            charter={charter}
            draft={draft}
            loading={chartersQuery.isPending}
            canManage={canManage && !dissolved}
            canDraft={canDraft && !dissolved}
            canImport={canImport && !dissolved}
            holdsLabel={Boolean(committee.charter_label)}
            starting={holdLabel.isPending}
            startError={
              holdLabel.isError
                ? isGivmoError(holdLabel.error)
                  ? holdLabel.error.message
                  : w.linkFailed
                : null
            }
            onStart={(mode) => holdLabel.mutate({ kind: 'version', mode })}
            onLink={() => setOpened({ kind: 'link' })}
          />
          <RulesCard
            committee={committee}
            boardsWay={boardsWay}
            canRecord={canRecordRules && !isDissolved(committee)}
            onAdd={() => setOpened({ kind: 'rule' })}
          />
        </div>
      </div>

      {opened?.kind === 'member' ? (
        <MemberDialog
          orgRef={org}
          committee={committee}
          member={opened.member}
          canInvite={canInvite}
          onClose={() => setOpened(null)}
          onDone={settle}
          onAdded={show}
        />
      ) : null}
      {opened?.kind === 'details' ? (
        <CommitteeDetailsDialog
          orgRef={org}
          committee={committee}
          onClose={() => setOpened(null)}
          onDone={settle}
        />
      ) : null}
      {opened?.kind === 'link' ? (
        <CharterLinkDialog
          orgRef={org}
          committee={committee}
          charters={charters}
          onClose={() => setOpened(null)}
          onDone={settle}
        />
      ) : null}
      {opened?.kind === 'dissolve' ? (
        <DissolveDialog
          orgRef={org}
          committee={committee}
          onClose={() => setOpened(null)}
          onDone={settle}
        />
      ) : null}
      {opened?.kind === 'correct' ? (
        <CorrectDissolutionDialog
          orgRef={org}
          committee={committee}
          onClose={() => setOpened(null)}
          onDone={settle}
        />
      ) : null}
      {opened?.kind === 'authority' ? (
        <AuthorityDialog
          orgRef={org}
          committee={committee}
          charter={charter}
          onClose={() => setOpened(null)}
          onDone={settle}
        />
      ) : null}
      {opened?.kind === 'practice' ? (
        <ConfirmationDialog
          orgRef={org}
          committee={committee}
          onClose={() => setOpened(null)}
          onDone={settle}
        />
      ) : null}
      {opened?.kind === 'rule' ? (
        <CommitteeRuleDialog
          orgRef={org}
          committee={committee}
          onClose={() => setOpened(null)}
          onDone={settle}
        />
      ) : null}
      {opened?.kind === 'schedule' ? (
        <MeetingFormDialog
          orgRef={org}
          meeting={null}
          initialCommitteeId={committee.id}
          onClose={() => setOpened(null)}
          onSaved={() => {
            setOpened(null);
            void detailQuery.refetch();
            void queryClient.invalidateQueries({
              queryKey: operations.officerListBoardCommitteesOptions({ path: { org_ref: org } })
                .queryKey,
            });
            void queryClient.invalidateQueries({
              queryKey: operations.officerListBoardMeetingsOptions({ path: { org_ref: org } })
                .queryKey,
            });
          }}
        />
      ) : null}
      {opened?.kind === 'version' ? (
        <VersionDialog
          orgRef={org}
          mode={opened.mode}
          modes={[
            ...(canDraft ? (['draft'] as const) : []),
            ...(canImport ? (['import'] as const) : []),
          ]}
          documents={charters}
          initialKind="committee_charter"
          initialLabel={committee.charter_label ?? committee.name}
          {...(opened.formId !== undefined ? { initialTemplateId: opened.formId } : {})}
          onClose={() => setOpened(null)}
          onSaved={() => {
            setOpened(null);
            void chartersQuery.refetch();
            void draftsQuery.refetch();
          }}
        />
      ) : null}
    </PageShell>
  );
}

function MembersCard({
  committee,
  canManage,
  onAdd,
  onChange,
}: {
  committee: BoardCommitteeOut;
  canManage: boolean;
  onAdd: () => void;
  onChange: (member: BoardCommitteeMemberOut) => void;
}) {
  const current = activeMembers(committee);
  const earlier = endedMembers(committee);
  return (
    <Card testId="committee-members">
      <CardBody className="flex flex-col gap-12">
        <div className="flex items-center justify-between gap-12">
          <h2 className={CARD_TITLE}>{w.membersTitle}</h2>
          {canManage ? (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={onAdd}
              testId="committee-add-member"
            >
              {w.addMember}
            </Button>
          ) : null}
        </div>
        {current.length > 0 ? (
          <MemberRows members={current} canManage={canManage} onChange={onChange} />
        ) : earlier.length === 0 ? (
          <p className={QUIET} data-testid="committee-no-members">
            {w.noMembers}
          </p>
        ) : null}
        {earlier.length > 0 ? (
          <div className="flex flex-col gap-8 pt-4" data-testid="committee-earlier-members">
            <GroupLabel>{w.formerMembers}</GroupLabel>
            <MemberRows members={earlier} canManage={false} onChange={onChange} />
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function MemberRows({
  members,
  canManage,
  onChange,
}: {
  members: readonly BoardCommitteeMemberOut[];
  canManage: boolean;
  onChange: (member: BoardCommitteeMemberOut) => void;
}) {
  return (
    <ul className="flex flex-col">
      {members.map((member) => (
        <li
          key={member.id}
          className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-16 gap-y-2 border-t border-border-subtle py-10 first:border-t-0 first:pt-0 max-sm:grid-cols-1"
          data-testid={`committee-member-${member.id}`}
        >
          <span className="flex min-w-0 flex-col gap-2">
            <Link
              to="/roster/$memberId"
              params={{ memberId: member.board_member_id }}
              className="font-sans text-s14 font-w600 tracking-givmo text-text-strong outline-none transition-colors hover:text-brand-700 focus-visible:underline"
            >
              {member.display_name ?? ''}
            </Link>
            <span className={PLAIN}>{memberFacts(member)}</span>
          </span>
          <span className="flex items-baseline gap-12 sm:justify-end">
            <span className={`${QUIET} tabular-nums`}>{memberDates(member)}</span>
            {canManage ? (
              <IconButton
                aria-label={w.changeMemberTitle(member.display_name ?? '')}
                tone="ghost"
                size="sm"
                className="-my-6 self-center"
                onClick={() => onChange(member)}
                testId={`committee-member-change-${member.id}`}
              >
                <Icon icon={Pencil} size={16} aria-hidden />
              </IconButton>
            ) : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The committee's meetings that stand, the latest first (frame c2): each by its day in its own zone and
 * its kind, and — once it has started — where its minutes are. A meeting with minutes opens on them.
 */
function MeetingsCard({ committee }: { committee: BoardCommitteeOut }) {
  const meetings = committee.meetings ?? [];
  const now = Date.now();
  return (
    <Card testId="committee-meetings">
      <CardBody className="flex flex-col gap-12">
        <h2 className={CARD_TITLE}>{cm.meetingsTitle}</h2>
        {meetings.length === 0 ? (
          <p className={QUIET} data-testid="committee-no-meetings">
            {cm.noMeetings}
          </p>
        ) : (
          <ul className="flex flex-col">
            {meetings.map((meeting) => {
              const started = meetingHasStarted(meeting.starts_at, now);
              const minutes = meeting.minutes ?? null;
              return (
                <li
                  key={meeting.meeting_id}
                  className="border-t border-border-subtle py-10 first:border-t-0 first:pt-0"
                  data-testid={`committee-meeting-${meeting.meeting_id}`}
                >
                  <Link
                    to={minutes !== null ? '/meetings/$meetingId/minutes' : '/meetings/$meetingId'}
                    params={{ meetingId: meeting.meeting_id }}
                    className="group grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-16 gap-y-2 outline-none max-sm:grid-cols-1"
                  >
                    <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong transition-colors group-hover:text-brand-700 group-focus-visible:underline">
                      {meetingDay(meeting)} · {meetingTypeLabel(meeting.meeting_type)}
                    </span>
                    {/* Minutes still to write or approve read plain; approved ones, done, quiet (c2). */}
                    {started ? (
                      <span
                        className={`${
                          minutes?.state === 'approved' || minutes?.state === 'imported'
                            ? QUIET
                            : PLAIN
                        } sm:text-right`}
                        data-testid={`committee-meeting-minutes-${meeting.meeting_id}`}
                      >
                        {cm.minutesState(minutes?.state ?? null)}
                      </span>
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

/** What a decision's line says of it: its day, and its tally (or that it was by written consent). */
function decidedLine(line: CommitteeDecisionLine): string {
  const day = displayDate(line.decidedOn);
  if (line.kind === 'written_consent') return cw.decidedLine(day, cw.byConsentShort);
  return cw.decidedLine(
    day,
    line.votesFor !== null && line.votesAgainst !== null
      ? cw.carriedTally(line.votesFor, line.votesAgainst)
      : cw.carried,
  );
}

/** The one board meeting every decision is suggested for, said once under them; null when they
 *  differ, none is named, or any decision is on an agenda (said once at the foot of a list holding
 *  one, it would read as that decision's too), and each then says its own. */
function sharedSuggestion(lines: readonly CommitteeDecisionLine[]) {
  if (lines.some((line) => line.onAgenda !== null)) return null;
  const first = lines[0]?.suggestedFor ?? null;
  if (first === null) return null;
  return lines.every((line) => line.suggestedFor?.meetingId === first.meetingId) ? first : null;
}

/** The one agenda item every decision is on (one press added them together), said once under them;
 *  null when they differ, or any isn't on an agenda (each then says its own). */
function sharedAgenda(lines: readonly CommitteeDecisionLine[]) {
  const first = lines[0]?.onAgenda ?? null;
  if (first === null || first.agendaItemId === null) return null;
  return lines.every((line) => line.onAgenda?.agendaItemId === first.agendaItemId) ? first : null;
}

function OnAgenda({
  onAgenda,
  testId,
}: {
  onAgenda: NonNullable<CommitteeDecisionLine['onAgenda']>;
  testId: string;
}) {
  return (
    <>
      <Link
        to="/meetings/$meetingId"
        params={{ meetingId: onAgenda.meetingId }}
        className={`${LINK} self-start`}
        data-testid={`${testId}-agenda`}
      >
        {cw.onAgendaOf(displayDate(onAgenda.on))}
      </Link>
      {onAgenda.boardMotion !== null ? (
        <p className={PLAIN} data-testid={`${testId}-motion`}>
          {cw.boardMotion(onAgenda.boardMotion.outcome, displayDate(onAgenda.boardMotion.on))}
        </p>
      ) : null}
    </>
  );
}

function DecisionsCard({
  committee,
  citation,
  canChange,
  dissolved,
  onChange,
}: {
  committee: BoardCommitteeOut;
  citation: string | null;
  canChange: boolean;
  /** A dissolved committee's decisions are suggested no more (the backend names no meeting to
   *  come): its card keeps the decisions and where each went, without the present-tense practice
   *  line. */
  dissolved: boolean;
  onChange: () => void;
}) {
  const lines = committeeDecisionLines(committee);
  const shared = sharedSuggestion(lines);
  const agenda = sharedAgenda(lines);
  return (
    <Card testId="committee-decisions">
      <CardBody className="flex flex-col gap-12">
        <div className="flex flex-col gap-4">
          <div className="flex items-baseline justify-between gap-12">
            <h2 className={CARD_TITLE}>{cw.pageTitle}</h2>
            {canChange ? (
              <button
                type="button"
                className={LINK}
                onClick={onChange}
                data-testid="committee-decisions-change"
              >
                {cw.change}
              </button>
            ) : null}
          </div>
          {dissolved ? null : (
            <p className={QUIET} data-testid="committee-decisions-practice">
              {cw.pageLine(citation)}
            </p>
          )}
        </div>
        {lines.length === 0 ? (
          <p className={QUIET} data-testid="committee-decisions-none">
            {cw.noneYet}
          </p>
        ) : (
          <ul className="flex flex-col">
            {lines.map((line) => (
              <li
                key={`${line.kind}:${line.id}`}
                className="flex flex-col gap-2 border-t border-border-subtle py-10 first:border-t-0 first:pt-0"
                data-testid={`committee-decision-${line.id}`}
              >
                <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong">
                  {line.words}
                </p>
                <p className={QUIET}>{keepRangesTogether(decidedLine(line))}</p>
                {line.onAgenda !== null ? (
                  agenda === null ? (
                    <OnAgenda onAgenda={line.onAgenda} testId={`committee-decision-${line.id}`} />
                  ) : null
                ) : shared === null && line.suggestedFor !== null ? (
                  <p className={QUIET} data-testid={`committee-decision-${line.id}-suggested`}>
                    {cw.suggestedFor(displayDate(line.suggestedFor.on))}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {shared !== null ? (
          <p className={QUIET} data-testid="committee-decisions-suggested-for">
            {cw.suggestedFor(displayDate(shared.on))}
          </p>
        ) : null}
        {agenda !== null ? (
          <div className="flex flex-col gap-2">
            <OnAgenda onAgenda={agenda} testId="committee-decisions" />
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function AuthorityCard({
  committee,
  canRecord,
  onRecord,
}: {
  committee: BoardCommitteeOut;
  canRecord: boolean;
  onRecord: () => void;
}) {
  const answer = ruleInForce(committee, 'authority');
  return (
    <Card testId="committee-authority">
      <CardBody className="flex flex-col gap-10">
        <div className="flex flex-col gap-2">
          <h2 className={CARD_TITLE}>{w.authorityTitle}</h2>
          <span className={QUIET}>{w.authoritySource}</span>
        </div>
        {answer === null ? (
          <p
            className="font-sans text-s14 font-w500 tracking-givmo text-text-secondary"
            data-testid="committee-authority-none"
          >
            {w.notAnswered}
          </p>
        ) : (
          <div className="flex flex-col gap-4" data-testid="committee-authority-answer">
            <p className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              {w.answerLabel(answer.choice_value)}
            </p>
            <p className={PLAIN}>{w.fromYourDocuments(answer.citation)}</p>
            {answer.recorded_at ? (
              <p className={QUIET}>{w.recordedOn(displayDate(answer.recorded_at))}</p>
            ) : null}
            {/* The committee's line-8 clock runs only under a "Yes" (the backend's §6.2). */}
            {answer.choice_value === 'broad' || answer.choice_value === 'particular_matters' ? (
              <p className={QUIET} data-testid="committee-authority-clock">
                {cm.clockRuns}
              </p>
            ) : null}
          </div>
        )}
        {canRecord ? (
          <button
            type="button"
            onClick={onRecord}
            className={`${LINK} self-start`}
            data-testid="committee-authority-record"
          >
            {answer === null ? w.recordAnswer : w.changeAnswer}
          </button>
        ) : null}
      </CardBody>
    </Card>
  );
}

function CharterCard({
  charter,
  draft,
  loading,
  canManage,
  canDraft,
  canImport,
  holdsLabel,
  starting,
  startError,
  onStart,
  onLink,
}: {
  charter: CharterInForce | null;
  draft: CharterDraft | null;
  loading: boolean;
  canManage: boolean;
  canDraft: boolean;
  canImport: boolean;
  holdsLabel: boolean;
  starting: boolean;
  startError: string | null;
  onStart: (mode: VersionDialogMode) => void;
  onLink: () => void;
}) {
  // A committee that holds no label first takes its own name's, which is composition's act.
  const mayStart = holdsLabel || canManage;
  return (
    <Card testId="committee-charter">
      <CardBody className="flex flex-col gap-8">
        <h2 className={CARD_TITLE}>{w.charterTitle}</h2>
        {loading ? (
          <Skeleton className="h-[40px] w-full rounded-md" testId="committee-charter-loading" />
        ) : charter === null && draft !== null ? (
          <>
            <Link
              to="/records/$recordId"
              params={{ recordId: draft.recordId }}
              className="font-sans text-s14 font-w600 tracking-givmo text-text-strong outline-none transition-colors hover:text-brand-700 focus-visible:underline"
              data-testid="committee-charter-draft"
            >
              {draft.title}
            </Link>
            <span className={PLAIN}>{w.charterDraftOnly}</span>
          </>
        ) : charter === null ? (
          <>
            <p className={QUIET} data-testid="committee-charter-none">
              {w.noCharter}
            </p>
            <div className="flex flex-col items-start gap-6">
              {canDraft && mayStart ? (
                <button
                  type="button"
                  className={LINK}
                  disabled={starting}
                  onClick={() => onStart('draft')}
                  data-testid="committee-charter-start"
                >
                  {w.startFromForm}
                </button>
              ) : null}
              {canImport && mayStart ? (
                <button
                  type="button"
                  className={LINK}
                  disabled={starting}
                  onClick={() => onStart('import')}
                  data-testid="committee-charter-import"
                >
                  {w.importCharter}
                </button>
              ) : null}
              {canManage ? (
                <button
                  type="button"
                  className={LINK}
                  onClick={onLink}
                  data-testid="committee-charter-link"
                >
                  {w.linkCharter}
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <Link
              to="/records/$recordId"
              params={{ recordId: charter.recordId }}
              className="font-sans text-s14 font-w600 tracking-givmo text-text-strong outline-none transition-colors hover:text-brand-700 focus-visible:underline"
              data-testid="committee-charter-record"
            >
              {charter.title}
            </Link>
            {charter.adoptedOn ? (
              <span className={PLAIN}>{w.charterVersion(displayDate(charter.adoptedOn))}</span>
            ) : null}
            {canDraft || canImport ? (
              <button
                type="button"
                className={`${LINK} self-start`}
                disabled={starting}
                onClick={() => onStart(canDraft ? 'draft' : 'import')}
                data-testid="committee-charter-version"
              >
                {w.addVersion}
              </button>
            ) : null}
          </>
        )}
        {startError !== null ? (
          <ErrorMessage testId="committee-charter-error">{startError}</ErrorMessage>
        ) : null}
      </CardBody>
    </Card>
  );
}

function RuleRow({
  label,
  committee,
  kind,
  boardsWay = null,
}: {
  label: string;
  committee: BoardCommitteeOut;
  kind: 'notice_period' | 'quorum' | 'minutes_approval';
  /** CBG-YR6-APPROVE: the minutes rule's stand-in while the committee records none. */
  boardsWay?: string | null;
}) {
  const rule = ruleInForce(committee, kind);
  return (
    <li
      className="flex flex-col gap-2 border-t border-border-subtle py-8 first:border-t-0 first:pt-0"
      data-testid={`committee-rule-${kind}`}
    >
      <span className={QUIET}>{label}</span>
      {rule !== null ? (
        <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
          {rule.value_summary}
          <span className="font-w400 text-text-secondary"> · {rule.citation}</span>
        </span>
      ) : boardsWay !== null ? (
        <>
          <span
            className="font-sans text-s14 font-w600 tracking-givmo text-text-strong"
            data-testid="committee-rule-minutes_approval-boards-way"
          >
            {w.wayLabel(boardsWay)}
          </span>
          <span className={QUIET}>{cm.fromYourBoardsWay}</span>
        </>
      ) : (
        <span className="font-sans text-s14 font-w500 tracking-givmo text-text-secondary">
          {w.notCurrentlyRecorded}
        </span>
      )}
    </li>
  );
}

function RulesCard({
  committee,
  boardsWay,
  canRecord,
  onAdd,
}: {
  committee: BoardCommitteeOut;
  boardsWay: string | null;
  canRecord: boolean;
  onAdd: () => void;
}) {
  return (
    <Card testId="committee-rules">
      <CardBody className="flex flex-col gap-10">
        <h2 className={CARD_TITLE}>{w.rulesTitle}</h2>
        <ul className="flex flex-col">
          <RuleRow label={w.noticeLabel} committee={committee} kind="notice_period" />
          <RuleRow label={w.quorumLabel} committee={committee} kind="quorum" />
          <RuleRow
            label={w.minutesWayLabel}
            committee={committee}
            kind="minutes_approval"
            boardsWay={boardsWay}
          />
        </ul>
        {canRecord ? (
          <button
            type="button"
            className={`${LINK} self-start`}
            onClick={onAdd}
            data-testid="committee-rules-add"
          >
            {w.addRule}
          </button>
        ) : null}
      </CardBody>
    </Card>
  );
}
